import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Button from '@mui/material/Button';

// mui icons
import Delete from '@mui/icons-material/Close';

import {
  createStdRequest,
  apiFetch,
  dateStringForDateTime,
  isNonLocalNonLoggedInVisitor,
  getPropertyFilterMenuItems,
  getDefaultPropertyFilterMenuItems,
  allMenuItemsAreChecked,
  filterIsInDefaultState,
} from '../utils/utils';

import {
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
  updateCompaniesFilter,
} from '../store/slices/intendedInfoSlice';
import { setPropertyFilterState } from '../store/slices/filterStatesSlice';
import { removeVisibleFirm } from '../store/slices/orgContactsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import DA from '../store/ReduxAccess';

import CompanyTableAppBar from './CompanyTableAppBar';
import NewReferrerOrgModal from './NewReferrerOrgModal';
import IcfReferrerFirmModal from './IcfReferrerFirmModal';
import ConfirmModal from './ConfirmModal';
import ColumnFilterIcon from './ColumnFilterIcon';

import pageTableStyles from '../assets/jss/crm/components/pageTableStyles.jsx';
import tableHeadStyles from '../assets/jss/crm/components/tableHeadStyles.jsx';

function desc(a, b, orderBy) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];
  if (typeof aValue === 'string') {
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();
  } else if (Array.isArray(aValue)) {
    aValue = aValue.length > 0 ? aValue[0].name.toLowerCase() : '';
    bValue = bValue.length > 0 ? bValue[0].name.toLowerCase() : '';
  }
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

let columns = [
  { id: 'name', label: 'Name' },
  { id: 'firmType', filterable: true, label: 'Company Category' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'temperature', filterable: true, label: 'Temp' },
  { id: 'daysSince', numeric: true, label: 'Inactive Days' },
  { id: 'lastContact', numeric: true, label: 'Last Contact' },
];

function handleEntered() {
  document.getElementsByTagName('body')[0].style.paddingRight = '0px';
}

const shouldShowButton = false;

class CompanyTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  createFilterHandler = (property) => (event) => {
    this.props.onRequestFilter(event, property);
  };

  render() {
    const { classes, order, orderBy, shouldHighlightFilterIcon } = this.props;

    const loggedInUserInfo = DA.getLoggedInUserInfo();
    const userIsAdmin = loggedInUserInfo && loggedInUserInfo.role === 'Admin';

    if (userIsAdmin && columns.length === 5 && shouldShowButton) {
      columns = columns.concat({ id: 'actions', label: 'Act' });
    }

    return (
      <TableHead>
        <TableRow className={classes.headerRow}>
          {columns.map(
            (column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? 'right' : 'left'}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.filterable && (
                  <ColumnFilterIcon
                    onClick={this.createFilterHandler(column.id)}
                    shouldHighlight={() => shouldHighlightFilterIcon(column.id)}
                  />
                )}

                {column.label}

                {!column.notSortable && (
                  <Tooltip title="Sort" placement={column.numeric ? 'bottom-end' : 'bottom-start'} enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={this.createSortHandler(column.id)}
                      classes={{
                        icon: orderBy === column.id ? classes.activeSortIcon : classes.inactiveSortIcon,
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}

CompanyTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
};

const StyledTableHead = withStyles(tableHeadStyles)(CompanyTableHead);

const styles = (theme) => ({
  ...pageTableStyles(theme),
  // override this for Company Table
  table: {
    minWidth: 820,
    '& .MuiTableCell-root': {
      fontSize: '1rem',
      whiteSpace: 'nowrap',
    },
  },
  actionButton: {
    margin: 0,
    padding: '5px',
    '& svg,& .fab,& .fas,& .far,& .fal,& .material-icons': {
      marginRight: '0px',
    },
  },
  icon: {
    verticalAlign: 'middle',
    width: '14px !important',
    height: '14px !important',
    top: '-1px',
    position: 'relative',
  },
  selectAllButton: {
    textTransform: 'none',
    fontSize: '18px',
  },
});

class CompanyTable extends React.Component {
  state = {
    order: 'asc',
    orderBy: 'name',
    autocompleteValue: '',
    showNewReferrerOrgModal: false,
    refreshDummy: 0,
    showFilter: false,
    filterItemList: [],
    filterBy: '',
  };

  firmTypeMenuItems = this.getFirmTypeMenuItems();
  temperatureMenuItems = this.getTemperatureMenuItems();

  defaultFirmTypeMenuItems = this.getDefaultMenuItems('Referrer Organization Type');
  defaultTemperatureMenuItems = this.getDefaultMenuItems('Referral Company Temperature');

  componentDidMount() {
    this.firmTypeMenuItems = this.getFirmTypeMenuItems();
    this.temperatureMenuItems = this.getTemperatureMenuItems();
  }

  getFirmTypeMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['referralCompaniesPage'];
    return getPropertyFilterMenuItems(pageFiltersState, 'firmType', 'Referrer Organization Type');
  }

  getTemperatureMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['referralCompaniesPage'];
    return getPropertyFilterMenuItems(pageFiltersState, 'temperature', 'Referral Company Temperature');
  }

  getDefaultMenuItems(dropdownName) {
    return getDefaultPropertyFilterMenuItems(dropdownName);
  }

  isFirmTypeMenuItemChecked(firmType) {
    const matchingFirmTypeItem = this.firmTypeMenuItems.find((item) => item.name === firmType);
    return matchingFirmTypeItem && matchingFirmTypeItem.checked;
  }

  isTemperatureMenuItemChecked(temperature) {
    const foundTemperatureItem = this.temperatureMenuItems.find((item) => item.name === temperature);
    return foundTemperatureItem && foundTemperatureItem.checked;
  }

  createFilterItemToggleHandler = (option) => () => {
    const copiedList = this.state.filterItemList.slice(0);
    copiedList[option].checked = !this.state.filterItemList[option].checked;

    const payload = {
      pageName: 'referralCompaniesPage',
      filterName: this.state.filterBy,
      propertyName: copiedList[option].name,
      value: copiedList[option].checked,
    };
    this.props.setPropertyFilterState(payload);

    const allSelected = allMenuItemsAreChecked(copiedList.slice(1));
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';
    copiedList[0].name = selectionButtonLabel;

    this.setState({ filterItemList: copiedList });
  };

  shouldHighlightFilterIcon(columnId) {
    if (columnId === 'firmType') {
      return this.shouldHighlightFirmTypeFilterIcon();
    } else if (columnId === 'temperature') {
      return this.shouldHighlightTemperatureFilterIcon();
    }
  }

  shouldHighlightFirmTypeFilterIcon() {
    return !filterIsInDefaultState(this.firmTypeMenuItems, this.defaultFirmTypeMenuItems);
  }

  shouldHighlightTemperatureFilterIcon() {
    return !filterIsInDefaultState(this.temperatureMenuItems, this.defaultTemperatureMenuItems);
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy });
  };

  showFilterMenu = (event, columnId) => {
    const { clientX, clientY } = event;
    let filterItemList = [];

    if (columnId === 'firmType') {
      filterItemList = this.firmTypeMenuItems;
    } else if (columnId === 'temperature') {
      filterItemList = this.temperatureMenuItems;
    }
    this.setState({
      showFilter: true,
      filterX: clientX,
      filterY: clientY,
      filterItemList,
      filterBy: columnId,
    });
  };

  handleFilterMenuClickAway = () => () => {
    this.setState({ showFilter: false });
  };

  handleSelectAllButtonClick = (itemName) => {
    const copiedList = this.state.filterItemList.slice(0);
    let shouldBeChecked;
    if (itemName === 'Select All') {
      copiedList[0].name = 'Unselect All';
      shouldBeChecked = true;
    } else {
      copiedList[0].name = 'Select All';
      shouldBeChecked = false;
    }

    copiedList.forEach((item) => {
      if (!item.name.endsWith('elect All')) {
        item.checked = shouldBeChecked;
      }
      const payload = {
        pageName: 'referralCompaniesPage',
        filterName: this.state.filterBy,
        propertyName: item.name,
        value: shouldBeChecked,
      };
      this.props.setPropertyFilterState(payload);
    });

    this.setState({ filterItemList: copiedList });
  };

  handleClick = (event, id) => {
    this.props.setIntendedReferrerCompanyId(id);
    this.setState({ redirect: 'referrerOrg' });
  };

  applyFilter(entries) {
    return entries.filter((e) => e && this.passFilter(e));
  }

  passFilter(entry) {
    return (
      entry && this.isFirmTypeMenuItemChecked(entry.firmType) && this.isTemperatureMenuItemChecked(entry.temperature)
    );
  }

  showNewCompanyForm = () => {
    this.setState({ showNewReferrerOrgModal: true });
  };

  augmentedCompaniesForTable(companyIds) {
    return companyIds.map((cid) => this.augmentedCompanyForId(cid));
  }

  augmentedCompanyForId(companyId) {
    const company = DA.getFirmById(companyId);
    if (!company) {
      console.log(`*** Couldn't find Firm with id: ${companyId} ***`);
      return null;
    }
    const clone = { ...company };
    if (!company.contactIds) {
      console.log(`Company '${company.name} (id:${company.firmId}) has undefined contacts`);
    }
    const contactIds = company.contactIds || [];
    clone.contactItemText = this.computeContactItemText(contactIds);
    clone.firstContactId = contactIds.length === 0 ? 0 : contactIds[0];
    clone.lastContact = this.lastContactDateWith(contactIds);
    clone.daysSince = this.daysSince(clone.lastContact);
    return clone;
  }

  daysSince(otherDate) {
    const floatDays = DateTime.local().diff(otherDate).as('days');
    return Math.trunc(floatDays);
  }

  computeContactItemText(contactIds) {
    if (!contactIds || contactIds.length === 0) {
      return '<None listed yet>';
    } else {
      const firstContactId = contactIds[0];
      const firstContactName = this.getNameForPersonWithId(firstContactId);
      if (contactIds.length === 1) {
        return firstContactName;
      } else {
        return firstContactName + ' ...';
      }
    }
  }

  getNameForPersonWithId(personId) {
    const person = DA.getPersonById(personId);
    return person.preferredFullName;
  }

  lastContactDateWith(contactIds) {
    if (contactIds.length === 0) {
      return DateTime.fromMillis(0);
    }
    const latestDatesForIds = contactIds.map((contactId) => DA.getPersonById(contactId).latestContactDate);
    return DateTime.max(...latestDatesForIds);
  }

  handleNewReferrerOrgSuccess(visibleFirmId) {
    this.setState({ showNewReferrerOrgModal: false });
  }

  handleNewReferrerOrgClose() {
    this.setState({ showNewReferrerOrgModal: false });
  }

  launchOrgContactPage(contactId, referrerOrgId) {
    this.props.setIntendedReferrerCompanyId(referrerOrgId);
    this.props.setIntendedReferrerContactId(contactId);
    this.setState({ redirect: 'referrerContact' });
  }

  removeFirm(firmId) {
    const visibleFirmName = DA.getFirmById(firmId).name;
    const q = 'Are you sure you want to forget about ' + visibleFirmName;
    this.setState({
      shouldShowDeletionConfirmationModal: true,
      deletionConfirmationQuery: q,
      deleteFunc: () => this.removeFirmAfterConfirmation(firmId),
    });
  }

  removeFirmAfterConfirmation(firmId) {
    const myFirmId = DA.getFirmId();
    const uri = `/api/firm/${myFirmId}/visibleFirm/${firmId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = (json) => {
      this.props.removeVisibleFirm(firmId);
      this.setState({ refreshDummy: this.state.refreshDummy + 1 });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  downloadCompanies = (filteredCompanies) => {
    this.maxPhonesPerLine = 0;
    this.maxAddressesPerLine = 0;
    const { order, orderBy } = this.state;
    const companies = stableSort(filteredCompanies, getSorting(order, orderBy));
    const lines = [];
    companies.forEach((company, i) => lines.push(this.lineForCompany(company, i)));
    lines.unshift(this.createHeaderLine());
    const fileContent = lines.join('\n');

    const blob = new Blob([fileContent], { type: 'text/comma-separated-values' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'companies.csv';
    a.click();
  };

  createHeaderLine() {
    const line = 'Count,Name,Phone,Website,Address,City,State,Zip';
    return line;
  }

  lineForCompany(company, i) {
    const firm = DA.getFirmById(company.firmId);
    const name = this.csvEscapedValue(firm.name);
    const phoneNumbers = firm.officePhone ? this.csvEscapedValue(firm.officePhone) : '';
    const addresses = firm.address;
    const webLinks = firm.webLinks;
    let line = `${i + 1},${name},${phoneNumbers}`;

    if (webLinks && webLinks.length > 0) {
      webLinks.forEach((webLinksObj) => {
        line += ',' + this.csvEscapedValue(webLinksObj.url);
      });
    } else {
      line += ',';
    }

    if (addresses && addresses.addressId > 0) {
      line += ',' + this.csvEscapedValue(addresses.line1);
      line += ',' + this.csvEscapedValue(addresses.city);
      line += ',' + this.csvEscapedValue(addresses.state);
      line += ',' + this.csvEscapedValue(addresses.zip);
    } else {
      line += ',';
    }
    return line;
  }

  csvEscapedValue(value) {
    if (!value) {
      return '';
    }
    let needsQuoteMarks = false;
    if (value.indexOf('"') >= 0) {
      needsQuoteMarks = true;
      value = value.replace('"', '""');
    }
    if (value.indexOf('\n') >= 0 || value.indexOf(',') >= 0) {
      needsQuoteMarks = true;
    }
    return needsQuoteMarks ? '"' + value + '"' : value;
  }

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;
    const firmId = DA.getFirmId();
    const companyIds = DA.getIdsOfFirmsVisibleToFirm(firmId);
    const augmentedCompanies = this.augmentedCompaniesForTable(companyIds);
    const { order, orderBy, filterItemList } = this.state;
    let filteredCompanies = this.applyFilter(augmentedCompanies);
    filteredCompanies = filteredCompanies.filter((co) => co.firmId !== firmId);
    const filtered =
      !filterIsInDefaultState(this.firmTypeMenuItems, this.defaultFirmTypeMenuItems) ||
      !filterIsInDefaultState(this.temperatureMenuItems, this.defaultTemperatureMenuItems);
    const summary = `${filtered ? 'Filtered' : 'All'} Referral Companies (${filteredCompanies.length})`;
    const StyledFilterDialog = styled(Dialog)`
      .MuiBackdrop-root {
        background-color: transparent;
      },
    `;

    const filterPosition = {
      position: 'absolute',
      left: this.state.filterX + 'px',
      top: this.state.filterY + 'px',
      margin: '5px',
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    };
    const filterStyle = { style: filterPosition };

    const loggedInUserInfo = DA.getLoggedInUserInfo();
    const userIsAdmin = loggedInUserInfo && loggedInUserInfo.role === 'Admin';

    return (
      <Paper className={classes.root}>
        {this.state.showNewReferrerOrgModal && (
          <IcfReferrerFirmModal
            firmType={null}
            onSuccess={(visibleFirmId) => this.handleNewReferrerOrgSuccess(visibleFirmId)}
            onCancel={() => this.handleNewReferrerOrgClose()}
          />
        )}

        {false && (
          <NewReferrerOrgModal
            show={this.state.showNewReferrerOrgModal}
            viewingFirmId={firmId}
            onSuccess={(visibleFirmId) => this.handleNewReferrerOrgSuccess(visibleFirmId)}
            onClose={() => this.handleNewReferrerOrgClose()}
          />
        )}

        <ConfirmModal
          show={this.state.shouldShowDeletionConfirmationModal}
          title="Confirm Deletion"
          confirmButtonLabel="Delete"
          rejectButtonLabel="Cancel"
          onConfirm={this.state.deleteFunc}
          onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
        >
          <div>{this.state.deletionConfirmationQuery}</div>
        </ConfirmModal>

        <CompanyTableAppBar
          summary={summary}
          newButtonLabel="New Company"
          newButtonFunc={() => this.showNewCompanyForm()}
          companies={filteredCompanies}
          onAutocompleteChange={(company) => this.handleClick(null, company.firmId)}
          downloadFunc={() => this.downloadCompanies(filteredCompanies)}
        />
        <div className={classes.tableWrapper}>
          <Table stickyHeader className={classes.table} aria-labelledby="tableTitle">
            <StyledTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
              onRequestFilter={this.showFilterMenu}
              shouldHighlightFilterIcon={(columnId) => this.shouldHighlightFilterIcon(columnId)}
            />
            <TableBody>
              {stableSort(filteredCompanies, getSorting(order, orderBy)).map((n, i) => {
                return (
                  <TableRow
                    tabIndex={-1}
                    key={n.firmId}
                    id={'company-row-' + n.firmId}
                    style={{ backgroundColor: i % 2 ? '#f7f7f7' : '#fff' }}
                  >
                    <TableCell
                      className={classes.clickableCell}
                      component="th"
                      scope="row"
                      padding="normal"
                      onClick={(event) => this.handleClick(event, n.firmId)}
                    >
                      {n.name}
                    </TableCell>
                    <TableCell>{n.firmType}</TableCell>
                    {n.firstContactId ? (
                      <TableCell
                        className={classes.clickableCell}
                        onClick={(event) => this.launchOrgContactPage(n.firstContactId, n.firmId)}
                      >
                        {n.contactItemText}
                      </TableCell>
                    ) : (
                      <TableCell>{n.contactItemText}</TableCell>
                    )}
                    <TableCell>{n.temperature}</TableCell>
                    <TableCell align="right">{n.daysSince > 1000 ? '' : n.daysSince}</TableCell>
                    <TableCell align="right">
                      {n.daysSince > 1000 ? '' : dateStringForDateTime(n.lastContact)}
                    </TableCell>

                    {userIsAdmin && shouldShowButton && (
                      <TableCell>
                        <IconButton
                          key="hide"
                          className={classes.actionButton}
                          onClick={() => this.removeFirm(n.firmId)}
                          size="large"
                        >
                          <Delete className={classes.icon} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <StyledFilterDialog
          open={this.state.showFilter}
          PaperComponent={Paper}
          PaperProps={filterStyle}
          onEntered={handleEntered}
        >
          <ClickAwayListener onClickAway={this.handleFilterMenuClickAway()}>
            <List>
              {filterItemList.map((item, i) => (
                <ListItem className={classes.ownerListItem} key={i}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {i === 0 && (
                      <Button
                        className={classes.selectAllButton}
                        onClick={() => this.handleSelectAllButtonClick(item.name)}
                      >
                        {item.name}
                      </Button>
                    )}
                    {i > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.checked}
                            onChange={this.createFilterItemToggleHandler(i)}
                            value={item.name}
                            color="primary"
                          />
                        }
                        label={item.name}
                        className={classes.ownerLabel}
                      />
                    )}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </StyledFilterDialog>
      </Paper>
    );
  }
}

CompanyTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  reduxPropertyFiltersState: state.filterStates.propertyFilters,
});

const actionProducers = {
  setApiResponseInfo,
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
  removeVisibleFirm,
  setPropertyFilterState,
  updateCompaniesFilter,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(CompanyTable));
